/*
  Cart drawer.
  - <cart-recommendations>: swaps the server-rendered cross-sell fallback for Shopify product recommendations
    (sections/cart-drawer-recommendations.liquid), complementary first and related next, when 2+ products come back.
  - Size sheet: Quick add on a shoe opens a sheet over the drawer to pick a size, then submits the card's
    product-form (Prestige adds to cart and re-renders the drawer).
*/
(() => {
  const recsCache = new Map();

  async function fetchRecs(productId, intent, limit, sectionId) {
    const key = `${productId}:${intent}:${limit}`;
    if (!recsCache.has(key)) {
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=${sectionId}`;
      recsCache.set(
        key,
        fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return [...doc.querySelectorAll('.cart-rec')];
          })
          .catch(() => [])
      );
    }
    return recsCache.get(key);
  }

  class CartRecommendations extends HTMLElement {
    async connectedCallback() {
      const productId = this.dataset.productId;
      const intent = this.dataset.intent || 'complementary';
      if (!productId || intent === 'none') return;
      const limit = Number(this.dataset.limit || 6);
      const sectionId = this.dataset.sectionId;
      let cards = await fetchRecs(productId, intent, limit, sectionId);
      if (cards.length < 2 && intent === 'complementary') cards = await fetchRecs(productId, 'related', limit, sectionId);
      if (cards.length < 2 || !this.isConnected) return;
      const track = this.querySelector('[data-recs-track]');
      track.replaceChildren(...cards.slice(0, limit).map((c) => document.importNode(c, true)));
      this.hidden = false;
    }
  }
  if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);

  // Size sheet
  let sheet = null;
  let lastTrigger = null;

  function closeSheet() {
    if (!sheet) return;
    sheet.remove();
    sheet = null;
    lastTrigger?.focus();
  }

  function openSheet(trigger) {
    const form = trigger.closest('form');
    const template = form?.querySelector('template[data-size-sheet]');
    if (!template) return;
    closeSheet();
    lastTrigger = trigger;

    sheet = document.createElement('div');
    sheet.className = 'size-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    // Copy comes from the template's data attributes (theme locale), so it stays editable in the admin.
    const copy = template.dataset;
    sheet.setAttribute('aria-label', copy.title || '');
    sheet.innerHTML = `
      <button type="button" class="size-sheet__overlay" data-size-sheet-close></button>
      <div class="size-sheet__panel">
        <button type="button" class="size-sheet__close" data-size-sheet-close>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14"/></svg>
        </button>
        <div data-size-sheet-body></div>
        <p class="size-sheet__eyebrow"><span data-size-sheet-label></span><span data-size-chosen></span></p>
        <div data-size-sheet-grid></div>
        <button type="button" class="cart-v2__primary size-sheet__submit" disabled data-size-sheet-submit></button>
      </div>`;
    sheet.querySelectorAll('[data-size-sheet-close]').forEach((button) => button.setAttribute('aria-label', copy.close || ''));
    sheet.querySelector('[data-size-sheet-label]').textContent = copy.label || '';
    const content = template.content.cloneNode(true);
    const sizes = content.querySelector('.size-sheet__sizes');
    sheet.querySelector('[data-size-sheet-body]').append(...[...content.childNodes].filter((n) => n !== sizes));
    sheet.querySelector('[data-size-sheet-grid]').replaceWith(sizes);

    const submit = sheet.querySelector('[data-size-sheet-submit]');
    submit.textContent = copy.title || '';
    let chosen = null;
    sheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-sheet-close]')) return closeSheet();
      const size = event.target.closest('.size-sheet__size');
      if (size && !size.disabled) {
        sheet.querySelectorAll('.size-sheet__size').forEach((b) => b.setAttribute('aria-checked', String(b === size)));
        chosen = size.dataset.variantId;
        sheet.querySelector('[data-size-chosen]').textContent = size.textContent.trim();
        submit.disabled = false;
        submit.textContent = copy.add || '';
        return;
      }
      if (event.target.closest('[data-size-sheet-submit]') && chosen) {
        form.querySelector('[data-rec-variant]').value = chosen;
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = copy.adding || '';
        form.requestSubmit();
      }
    });
    sheet.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeSheet();
      }
    });

    const drawer = trigger.closest('cart-drawer');
    drawer.append(sheet);
    sheet.querySelector('.size-sheet__size:not([disabled])')?.focus();
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('cart-drawer [data-size-sheet-open]');
    if (trigger) {
      event.preventDefault();
      openSheet(trigger);
    }
  });

  // Escape inside the sheet closes only the sheet (capture runs before the drawer's own handler).
  document.addEventListener(
    'keydown',
    (event) => {
      if (sheet && event.key === 'Escape') {
        event.stopImmediatePropagation();
        closeSheet();
      }
    },
    true
  );

  // Emptying the cart: Prestige fades the footer out and never brings it back, because its empty cart has no footer.
  // Ours keeps Shop all there, so once the empty drawer is in place, drop that fade and bring the footer back in the
  // same way Prestige brings the body in.
  document.addEventListener('cart-drawer:refreshed', (event) => {
    const drawer = event.target;
    if (!drawer.classList?.contains('drawer--center-body')) return;
    const footer = drawer.shadowRoot?.querySelector('[part~="footer"]');
    if (!footer) return;
    footer.getAnimations().forEach((animation) => animation.cancel());
    footer.style.removeProperty('opacity');
    footer.style.removeProperty('transform');
    footer.animate(
      { opacity: [0, 1], transform: ['translateY(30px)', 'translateY(0)'] },
      { duration: 250, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' }
    );
  });

  document.addEventListener('cart:change', (event) => {
    closeSheet();
    const detail = event.detail || {};
    if (detail.baseEvent !== 'variant:add') return;
    const added = (detail.items || detail.cart?.items || [])[0];
    const live = document.querySelector('cart-drawer [data-cart-live]');
    if (live && added) live.textContent = (live.dataset.added || '').replace('[title]', added.product_title || added.title);
  });
})();
